import validator from 'validator';
import { buildMaskRegex, reformatToMask } from '../utils';

export const required = (value) => {

    // Check undefined or null
    if (value === undefined || value === null) return false;

    // Boolean — both true and false are valid selections
    if (typeof value === "boolean") return true;

    // Number — 0 is a valid, meaningful value (zero income, "Through Link" option keyed at 0,
    // etc.), so only genuinely non-numeric values (NaN) should fail here.
    if (typeof value === "number") return !isNaN(value);

    // String
    if (typeof value === "string") return value.trim() !== "";

    // Array
    if (Array.isArray(value)) return value.length > 0;

    // Date
    if (value instanceof Date) return !isNaN(value.getTime());

    // Object
    if (typeof value === "object") {
        return Object.keys(value).length > 0;
    }

    return true;
};

// Empty is valid here — whether the field is required is the REQUIRE rule's job
export const email = (value) => value === undefined || value === null || String(value).trim() === ""
    || validator.isEmail(String(value));

export const number = (value) => value === 0 || !isNaN(value);

export const maxLength = (value, maxLen) => String(value).trim().length <= maxLen;

export const minLength = (value, minLen) => String(value).trim().length >= minLen;

export const apaarId = (value, length) => {
    if (!value) return true;
    return String(value).trim().length === length;
}

export const compare = (value, compareValue) => value === compareValue;

// export const compare = (value, compareValue) => {
//     const isMatch = value === compareValue;
//     return {
//         isValid: isMatch,
//         customMessage: isMatch ? '' : 'Passwords do not match'
//     };
// }

export const distinct = (value, compareValue) => value !== '' && value !== compareValue;

export const maxProspects = (value, maxProspectsValue) => value <= maxProspectsValue;

export const minEndDate = (date, minEndDateValue) => date >= minEndDateValue;

export const maxSum = (sum, maxSumValue) => sum <= maxSumValue;

export const password = (value, props, components) => value === components['confirm'][0].value ? undefined : <span className="error">Passwords are not equal.</span>;

export const uniqueIdentifier = (value) => !!(value && String(value).trim().length && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value));

export const isvalidPassword = (value) => {
    if (!value || !String(value).trim().length || value.length < 8) return false;

    // ✅ Mandatory checks
    const hasUppercase = /[A-Z]/.test(value);
    if (!hasUppercase) return false;

    // ✅ Optional categories (need any 2 of these 3)
    let fulfilledCategories = 0;
    if (/[a-z]/.test(value)) fulfilledCategories++;
    if (/\d/.test(value)) fulfilledCategories++;
    if (/[!@#$%^&*]/.test(value)) fulfilledCategories++;

    return fulfilledCategories >= 2;
};

export const isValidEIN = (value) => {
    const unmaskedValue = value.replace(/\D/g, '')
    const isValid = /^\d{9}$/.test(unmaskedValue);
    return isValid;
};

export const isValidPhone = (value) => {
    const unmaskedValue = value.replace(/\D/g, '')
    const isValid = /^\d{10,15}$/.test(unmaskedValue);
    return isValid;
};

export const isValidFax = (value) => {
    const unmaskedValue = value.replace(/\D/g, '')
    const isValid = /^\d{10}$/.test(unmaskedValue);
    return isValid;
};

export const isWebsite = (value) => !!(
    value &&
    String(value).trim().length &&
    /^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+(\S*)$/.test(value)
);

export const isUnique = (value) => {
    const invalidChars = /[^a-zA-Z0-9\-& ]/;
    return !invalidChars.test(value);
};

export const isOnlyText = (value) => {
    const invalidChars = /^\D*$/;
    return invalidChars.test(value);
};

export const isInvalidBoundarySpaces = (value) => {
    // Otherwise, check if the string contains spaces only between characters
    const validPattern = /^[^\s][a-zA-Z\s]*[^\s]$/;
    return validPattern.test(value);
};

export const validateCharacters = (value) => {
    const specialCharsRegex = /^[A-Za-z0-9]+$/;
    // Returns true if the string contains any special character
    return specialCharsRegex.test(value);
};

export const isGradePointFormat = (value, format = "000.00", maxValue = 100) => {

    const [integerPart, decimalPart] = format.split('.');

    const integerDigits = integerPart.length;
    const decimalDigits = decimalPart.length;

    const regex = new RegExp(
        `^\\d{1,${integerDigits}}${decimalDigits
            ? `(?:\\.\\d{1,${decimalDigits}})?`
            : ''
        }$`
    );

    if (!regex.test(value)) {
        return false;
    }

    return Number(value) <= maxValue;
};

export const isValidMaskFormat = (value, format) => {
    if (value === undefined || value === null || String(value).trim() === "") return true;
    const regex = buildMaskRegex(format);
    if (!regex) return true;
    if (typeof value === "number" && format?.includes(".") && !/[Xx]/.test(format)) {
        const decimalDigits = format.split(".")?.[1]?.length ?? 0;
        return regex.test(value.toFixed(decimalDigits));
    }

    const str = String(value);
    if (regex.test(str)) return true;

    return regex.test(reformatToMask(str, format));
};

export const isValidZipCode = (rule, value) => {
    const patternData = getPostalCodePattern(value?.countryId || 45);
    if (patternData?.pattern) {
        const isValid = patternData.pattern.test(value?.pincode);
        const lengthMessage = patternData.minLength === patternData.maxLength ? `${patternData.minLength}`
            : `${patternData.minLength} or ${patternData.maxLength}`;
        return {
            isValid: isValid,
            customMessage: rule.message.replace('{length}', lengthMessage || '')
        };
    } else {
        const lengthMessage = patternData.minLength === patternData.maxLength ? `${patternData.minLength}`
            : `${patternData.minLength} or ${patternData.maxLength}`;
        return {
            isValid: true,
            customMessage: rule.message.replace('{length}', lengthMessage || '')
        };
    }
};

const getPostalCodePattern = (country) => {
    switch (country) {
        case 11: // Argentina: B1234ABC
            return { pattern: /^[A-Za-z]\d{4}[A-Za-z]{3}$/, minLength: 8, maxLength: 8 };
        /**
         * Australia, Switzerland, South Africa, Norway, Denmark, 
         * Belgium, Austria, New Zealand, Hungary, Luxembourg  
         **/
        case 14: case 15: case 22: case 59: case 99:
        case 127: case 158: case 165: case 204: case 214:
            return { pattern: /^\d{4}$/, minLength: 4, maxLength: 4 };
        case 31: // Brazil: 12345-678
            return { pattern: /^\d{5}-\d{3}$/, minLength: 9, maxLength: 9 };
        case 39: // Canada: A1A 1A1
            return { pattern: /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/, minLength: 6, maxLength: 7 };
        /**
         * France, Germany, Italy, Spain, Mexico, South Korea, 
         * Finland, Turkey, Saudi Arabia, Malaysia, UAE
         **/
        case 75: case 74: case 82: case 107: case 116: case 194:
        case 207: case 225: case 132: case 142: case 231:
            return { pattern: /^\d{5}$/, minLength: 5, maxLength: 5 };
        /**
         * India, Russia, China, Singapore
         **/
        case 45: case 101: case 182: case 199:
            return { pattern: /^\d{6}$/, minLength: 6, maxLength: 6 };
        case 58: // Czech Republic: 123 45
            return { pattern: /^\d{3}\s?\d{2}$/, minLength: 5, maxLength: 6 };
        case 85: // Greece: 123 45
            return { pattern: /^\d{3}\s?\d{2}$/, minLength: 5, maxLength: 6 };
        case 100: // Iceland: 101
            return { pattern: /^\d{3}$/, minLength: 3, maxLength: 3 };
        case 106: // Israel: 12345 or 1234567
            return { pattern: /^\d{5,7}$/, minLength: 5, maxLength: 7 };
        case 109: // Japan: 123-4567
            return { pattern: /^\d{3}-\d{4}$/, minLength: 8, maxLength: 8 };
        case 135: // Malta: ABC 1234
            return { pattern: /^[A-Za-z]{3}\s?\d{4}$/, minLength: 7, maxLength: 8 };
        case 156: // Netherlands: 1234 AB
            return { pattern: /^\d{4}\s?[A-Za-z]{2}$/, minLength: 6, maxLength: 6 };
        case 176: // Poland: 12-345
            return { pattern: /^\d{2}-\d{3}$/, minLength: 6, maxLength: 6 };
        case 177: // Portugal: 1234-567
            return { pattern: /^\d{4}-\d{3}$/, minLength: 8, maxLength: 8 };
        case 213: // Sweden: 123 45
            return { pattern: /^\d{3}\s?\d{2}$/, minLength: 5, maxLength: 6 };
        case 232: // UK: AA9A 9AA
            return { pattern: /^[A-Za-z]{1,2}\d{1,2}[A-Za-z]?\d[A-Za-z]{2}$/, minLength: 6, maxLength: 8 };
        case 233: // US: 12345 or 12345-6789
            return { pattern: /^\d{5}(-\d{4})?$/, minLength: 5, maxLength: 10 };
        default:
            return { pattern: null, minLength: 5, maxLength: 10 };
    }
};

const getFileExtension = (fileName) => {
    return fileName?.slice(fileName.lastIndexOf('.')).toLowerCase(); // Extracts the extension with the dot
};

export const validateFile = (file, formField) => {

    const fieldData = formField?.fieldSetting || formField || {};
    const fileAcceptedFormats = fieldData?.acceptedFormats || fieldData?.acceptedFileFormats;
    const maxSizeMB = fieldData?.maxFileSizeInMB;
    const minSizeMB = fieldData?.minFileSizeInMB;

    const validationState = {
        isValid: true,
        errorMessage: ``
    }

    if (!file) {
        // Return consistent object structure, falsy value is considered valid (e.g. optional fields)
        return validationState;
    }

    // The file upload stores [{ file, fileName, base64Data }] and a saved file is { file: { name }, base64Data } —
    // validate every item against the underlying File / its name
    if (Array.isArray(file)) {
        return file.map((item) => validateFile(item, formField)).find((result) => !result.isValid) ?? validationState;
    }
    if (file.file || file.fileName) {
        const name = file.file?.name ?? file.fileName;
        return validateFile({ name, size: file.file instanceof File ? file.file.size : undefined }, formField);
    }

    // No accepted formats configured — any format is allowed
    if (!fileAcceptedFormats?.length) {
        return validationState;
    }

    // Check file format
    const fileExtension = getFileExtension(file.name);
    if (fileAcceptedFormats.includes('*') || fileAcceptedFormats.includes(fileExtension)) {
        // If "ANY" is included or the file extension is valid, set isValid to true
        validationState.isValid = true;
    } else {
        // Otherwise, mark it as invalid and show an error message
        validationState.isValid = false;
        validationState.errorMessage = `Invalid file format. Accepted formats: ${fileAcceptedFormats.join(', ')}.`;
    }
    // Check file size
    const fileSizeMB = file.size / 1024 / 1024; // Convert bytes to MB
    if (fileSizeMB > maxSizeMB) {
        validationState.isValid = false;
        validationState.errorMessage = `File size exceeds the maximum limit of ${maxSizeMB} MB.`;
    }
    if (fileSizeMB < minSizeMB) {
        validationState.isValid = false;
        validationState.errorMessage = `File size must be at least ${minSizeMB} MB.`;
    }
    return validationState; // File is valid
};
export const validateImageFile = (file, fieldSettings, imageData) => {

    const {
        acceptedImageFormats,
        maxImageHeight,
        maxImageWidth,
        minImageHeight,
        minImageWidth,
        maxImageSizeInMB,
        minImageSizeInMB,
    } = fieldSettings;

    const validationState = {
        isValid: true,
        errorMessage: ``
    }

    if (!file) {
        return validationState;
    }

    // Check file format
    const fileExtension = getFileExtension(file.name);
    if (acceptedImageFormats?.includes('*') || acceptedImageFormats?.some(acceptable => acceptable === fileExtension)) {
        validationState.isValid = true;
    } else {
        validationState.isValid = false;
        validationState.errorMessage = `Invalid file format. Accepted formats: ${acceptedImageFormats?.join(', ')}.`;
        return validationState;
    }

    // Check file size
    const fileSizeMB = file.size / 1024 / 1024; // Convert bytes to MB
    if (fileSizeMB > maxImageSizeInMB) {
        validationState.isValid = false;
        validationState.errorMessage = `File size exceeds the maximum limit of ${maxImageSizeInMB} MB.`;
        return validationState;
    }

    if (fileSizeMB < minImageSizeInMB) {
        validationState.isValid = false;
        validationState.errorMessage = `File size must be at least ${minImageSizeInMB} MB.`;
        return validationState;
    }

    const { height, width } = imageData;

    if (width > maxImageWidth || height > maxImageHeight) {

        validationState.isValid = false;
        validationState.errorMessage = `Image dimensions exceed the maximum limit of ${maxImageWidth}x${maxImageHeight} pixels.`;

    } else if (width < minImageWidth || height < minImageHeight) {

        validationState.isValid = false;
        validationState.errorMessage = `Image dimensions must be at least ${minImageWidth}x${minImageHeight} pixels.`;

    }
    return validationState;
};

const parseDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
    if (typeof value === 'string') {
        const cleanStr = value.split('T')[0];
        if (cleanStr.includes('-')) {
            const parts = cleanStr.split('-');
            if (parts.length === 3) {
                const year = Number(parts[0]);
                const month = Number(parts[1]);
                const day = Number(parts[2]);
                if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                    return new Date(year, month - 1, day);
                }
            }
        } else if (cleanStr.includes('/')) {
            const parts = cleanStr.split('/');
            if (parts.length === 3) {
                const day = Number(parts[0]);
                const month = Number(parts[1]);
                const year = Number(parts[2]);
                if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
                    return new Date(year, month - 1, day);
                }
            }
        }
        const parsed = new Date(cleanStr);
        return isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
};

export const validateMaxDate = (value, rule, state) => {
    if (!value) return true;
    const valDate = parseDateValue(value);
    if (!valDate) return false;

    let maxDateObj = rule.maxDate;
    if (rule.maxDateFrom && state && state[rule.maxDateFrom]) {
        const fromDate = parseDateValue(state[rule.maxDateFrom]);
        if (fromDate) {
            maxDateObj = new Date(fromDate);
            if (rule.maxDaysOffset) {
                maxDateObj.setDate(maxDateObj.getDate() + rule.maxDaysOffset);
            }
        }
    }

    if (!maxDateObj) return true;

    const max = new Date(maxDateObj);
    max.setHours(23, 59, 59, 999);

    return valDate <= max;
};

export const validateMinDate = (value, rule, state) => {
    if (!value) return true;
    const valDate = parseDateValue(value);
    if (!valDate) return false;

    let minDateObj = rule.minDate;
    if (rule.minDateFrom && state && state[rule.minDateFrom]) {
        const fromDate = parseDateValue(state[rule.minDateFrom]);
        if (fromDate) {
            minDateObj = new Date(fromDate);
            if (rule.daysOffset) {
                minDateObj.setDate(minDateObj.getDate() + rule.daysOffset);
            }
        }
    }

    if (!minDateObj) return true;

    const min = new Date(minDateObj);
    min.setHours(0, 0, 0, 0);

    return valDate >= min;
};